import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { FaBold, FaItalic, FaUnderline, FaStrikethrough, FaCode, FaLink, FaUnlink, FaListUl, FaListOl, FaQuoteRight, FaImage, FaUndo, FaRedo, FaEraser, FaAlignLeft, FaAlignCenter, FaAlignRight, FaAlignJustify, FaMinus } from 'react-icons/fa';

const BLOCK = 'LStudio__app_rte';

/*
    🔥 EDITOR — TipTap

    Está construido sobre ProseMirror, el mismo motor que usan Notion,
    Linear y GitLab. La diferencia con Quill:

      · Quill trae su propia barra y su propio CSS. Rápido de poner,
        difícil de personalizar.
      · TipTap es HEADLESS: no dibuja nada. La barra es nuestra, así
        que respeta los tokens sin pelear con CSS de terceros.

    Por eso este archivo es más largo: la barra la escribimos nosotros.

    npm i @tiptap/react @tiptap/starter-kit @tiptap/extension-link
          @tiptap/extension-underline @tiptap/extension-text-align
          @tiptap/extension-placeholder @tiptap/extension-image
*/

const ToolButton = ({ onClick, isActive, isDisabled, title, children }) => (
    <button type="button" className={`${BLOCK}__tool`} data-on={isActive}
        title={title} aria-label={title} disabled={isDisabled}
        onMouseDown={(event) => event.preventDefault()}   /* no perder el foco del editor */
        onClick={onClick} >
        {children}
    </button>
);

export const RichTextTipTap = ({
    value, onChange, placeholder = 'Escribí acá…',
    isDisabled = false, minHeight = 160,
}) => {

    const editor = useEditor({
        editable: !isDisabled,
        content: value ?? '',
        extensions: [
            StarterKit.configure({ heading: { levels: [2, 3, 4] } }),
            Underline,
            Link.configure({ openOnClick: false, autolink: true }),
            Image.configure({ inline: false }),
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Placeholder.configure({ placeholder }),
        ],
        onUpdate: ({ editor: instance }) => onChange?.(instance.getHTML()),
    });

    /*
        ⚠️ SIN ESTO EL EDITOR SE BORRA SOLO

        TipTap mantiene su propio estado interno. Si el padre cambia
        `value` (un reset del formulario, cargar datos de la API), el
        editor no se entera. Y si sincronizamos sin comparar, cada
        pulsación reescribe el contenido y el cursor salta al inicio.
    */
    useEffect(() => {

        if (!editor) return;

        const CURRENT = editor.getHTML();

        if ((value ?? '') !== CURRENT) {
            editor.commands.setContent(value ?? '', false);
        }

    }, [value, editor]);

    useEffect(() => {
        editor?.setEditable(!isDisabled);
    }, [isDisabled, editor]);

    if (!editor) return null;

    const onSetLink = () => {

        const PREVIOUS = editor.getAttributes('link').href;
        const URL = window.prompt('Dirección del enlace', PREVIOUS ?? 'https://');

        if (URL === null) return;

        if (URL === '') return editor.chain().focus().extendMarkRange('link').unsetLink().run();

        editor.chain().focus().extendMarkRange('link').setLink({ href: URL }).run();
    };

    const onAddImage = () => {
        const URL = window.prompt('Dirección de la imagen', 'https://');
        if (URL) editor.chain().focus().setImage({ src: URL }).run();
    };

    return (
        <div className={`${BLOCK} ${BLOCK}--tiptap`} data-disabled={isDisabled}
            style={{ '--LS__rte_min_height': `${minHeight}px` }} >

            <div className={`${BLOCK}__toolbar`}>

                <div className={`${BLOCK}__group`}>
                    <select className={`${BLOCK}__select`}
                        value={
                            editor.isActive('heading', { level: 2 }) ? 'h2'
                                : editor.isActive('heading', { level: 3 }) ? 'h3'
                                    : editor.isActive('heading', { level: 4 }) ? 'h4' : 'p'
                        }
                        onChange={(event) => {
                            const V = event.target.value;
                            if (V === 'p') return editor.chain().focus().setParagraph().run();
                            editor.chain().focus().toggleHeading({ level: Number(V[1]) }).run();
                        }} >
                        <option value="p">Normal</option>
                        <option value="h2">Título 1</option>
                        <option value="h3">Título 2</option>
                        <option value="h4">Título 3</option>
                    </select>
                </div>

                <div className={`${BLOCK}__group`}>
                    <ToolButton title="Negrita" isActive={editor.isActive('bold')}
                        onClick={() => editor.chain().focus().toggleBold().run()}><FaBold /></ToolButton>
                    <ToolButton title="Cursiva" isActive={editor.isActive('italic')}
                        onClick={() => editor.chain().focus().toggleItalic().run()}><FaItalic /></ToolButton>
                    <ToolButton title="Subrayado" isActive={editor.isActive('underline')}
                        onClick={() => editor.chain().focus().toggleUnderline().run()}><FaUnderline /></ToolButton>
                    <ToolButton title="Tachado" isActive={editor.isActive('strike')}
                        onClick={() => editor.chain().focus().toggleStrike().run()}><FaStrikethrough /></ToolButton>
                    <ToolButton title="Código" isActive={editor.isActive('code')}
                        onClick={() => editor.chain().focus().toggleCode().run()}><FaCode /></ToolButton>
                </div>

                <div className={`${BLOCK}__group`}>
                    <ToolButton title="Lista con viñetas" isActive={editor.isActive('bulletList')}
                        onClick={() => editor.chain().focus().toggleBulletList().run()}><FaListUl /></ToolButton>
                    <ToolButton title="Lista numerada" isActive={editor.isActive('orderedList')}
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}><FaListOl /></ToolButton>
                    <ToolButton title="Cita" isActive={editor.isActive('blockquote')}
                        onClick={() => editor.chain().focus().toggleBlockquote().run()}><FaQuoteRight /></ToolButton>
                    <ToolButton title="Separador"
                        onClick={() => editor.chain().focus().setHorizontalRule().run()}><FaMinus /></ToolButton>
                </div>

                <div className={`${BLOCK}__group`}>
                    <ToolButton title="Izquierda" isActive={editor.isActive({ textAlign: 'left' })}
                        onClick={() => editor.chain().focus().setTextAlign('left').run()}><FaAlignLeft /></ToolButton>
                    <ToolButton title="Centro" isActive={editor.isActive({ textAlign: 'center' })}
                        onClick={() => editor.chain().focus().setTextAlign('center').run()}><FaAlignCenter /></ToolButton>
                    <ToolButton title="Derecha" isActive={editor.isActive({ textAlign: 'right' })}
                        onClick={() => editor.chain().focus().setTextAlign('right').run()}><FaAlignRight /></ToolButton>
                    <ToolButton title="Justificado" isActive={editor.isActive({ textAlign: 'justify' })}
                        onClick={() => editor.chain().focus().setTextAlign('justify').run()}><FaAlignJustify /></ToolButton>
                </div>

                <div className={`${BLOCK}__group`}>
                    <ToolButton title="Enlace" isActive={editor.isActive('link')}
                        onClick={onSetLink}><FaLink /></ToolButton>
                    <ToolButton title="Quitar enlace" isDisabled={!editor.isActive('link')}
                        onClick={() => editor.chain().focus().unsetLink().run()}><FaUnlink /></ToolButton>
                    <ToolButton title="Imagen" onClick={onAddImage}><FaImage /></ToolButton>
                </div>

                <div className={`${BLOCK}__group ${BLOCK}__group--end`}>
                    <ToolButton title="Deshacer" isDisabled={!editor.can().undo()}
                        onClick={() => editor.chain().focus().undo().run()}><FaUndo /></ToolButton>
                    <ToolButton title="Rehacer" isDisabled={!editor.can().redo()}
                        onClick={() => editor.chain().focus().redo().run()}><FaRedo /></ToolButton>
                    <ToolButton title="Limpiar formato"
                        onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}><FaEraser /></ToolButton>
                </div>

            </div>

            <EditorContent editor={editor} className={`${BLOCK}__content`} />

        </div>
    );
};
